import { createContext, useContext, ReactNode, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { translations, Locale, Translations } from "./translations";
import { setAnalyticsLanguage } from "@/lib/analytics";

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translations;
  localePath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const locale: Locale = lang === "en" ? "en" : "cs";
  const t = translations[locale];

  // Keep <html lang="..."> in sync with active locale for accessibility (Lighthouse a11y rule).
  useEffect(() => {
    if (typeof document !== "undefined" && document.documentElement.lang !== locale) {
      document.documentElement.lang = locale;
    }
    // Make active locale available to GA4 helpers so every event is auto-tagged
    // with `language: 'cs' | 'en'`.
    setAnalyticsLanguage(locale);
  }, [locale]);

  const setLocale = (newLocale: Locale) => {
    try {
      localStorage.setItem("user-locale", newLocale);
    } catch (e) {
      // localStorage may be unavailable in private mode or crawlers; ignore.
    }
    const currentPath = location.pathname.replace(/^\/(cs|en)/, "") || "/";
    navigate(`/${newLocale}${currentPath === "/" ? "" : currentPath}`);
  };

  const localePath = useMemo(() => {
    return (path: string) => `/${locale}${path === "/" ? "" : path}`;
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t, localePath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
